import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import * as api from 'lib/api';
import Button from 'components/Button';
import { MdDescription, MdAccessTime } from 'react-icons/md';
import { convertFromUtcToTimezone, minsToHours } from 'lib/helpers-time';
import { permissions } from 'config/permissions';
import Feature from 'components/Feature';
import Loading from 'components/Loading';
import { hasSubmissionDetails } from './timesheet-helpers';
import AdminTimesheetSubmission from './AdminTimesheetSubmission';
import TimesheetApproveModal from './TimesheetApproveModal';
import { StartEndMultiTimesheetSubmission } from './StartEndMultiTimesheetSubmission';

const formatTime = (date, timezone) => convertFromUtcToTimezone(date, timezone).format('HH:mm');

export const TimesheetSubmission = (props) => {
  const timesheetKey = props.booking.timesheetKey;
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const hasBookingDetails = hasSubmissionDetails(props.booking, props.shift.timesheetTypeKey);
  const [recordedTimesheetKey, setRecordedTimesheetKey] = useState(null);
  const [loadedSubmission, setLoadedSubmission] = useState(null);
  const [detailsLookup, setDetailsLookup] = useState(null);
  const markSubmissionRecorded = useCallback(() => setRecordedTimesheetKey(timesheetKey), [timesheetKey]);
  const submissionStatus = props.booking.submissionStatus ?? props.booking.timesheetStatus;
  const knownSubmission = !!props.submissionRecorded || (!!submissionStatus && submissionStatus !== 'archived') || !!props.booking.latestTimesheetSubmissionKey || (recordedTimesheetKey !== null && recordedTimesheetKey === timesheetKey);
  const lookupState = detailsLookup?.timesheetKey === timesheetKey ? detailsLookup.state : 'loading';
  const loadingDetails = !hasBookingDetails && lookupState === 'loading';
  const submissionRecorded = hasBookingDetails || knownSubmission || lookupState === 'recorded';
  const noSubmission = !submissionRecorded && lookupState === 'empty';

  useEffect(() => {
    let active = true;
    setLoadedSubmission(null);
    if (hasBookingDetails && !props.onDetailsLoaded) return () => { active = false; };
    setDetailsLookup({ timesheetKey, state: 'loading' });
    const load = async () => {
      try {
        const response = await api.get('timesheets/submission-details', { timesheetKey });
        if (!active) return;
        const body = response.body;
        if (response.status === 200 && body?.submission?.timesheetKey === timesheetKey) {
          setLoadedSubmission(body.submission);
          setDetailsLookup({ timesheetKey, state: 'recorded', isArchived: body.isArchived ?? body.submission.isArchived });
          props.onDetailsLoaded?.(timesheetKey, body);
        } else if (response.status === 200 && body?.submission === null && typeof body.submissionRecorded === 'boolean') {
          setDetailsLookup({ timesheetKey, state: body.submissionRecorded ? 'recorded' : 'empty', isArchived: body.isArchived });
          props.onDetailsLoaded?.(timesheetKey, body);
        } else {
          setDetailsLookup({ timesheetKey, state: 'error' });
        }
      } catch (error) {
        if (active) setDetailsLookup({ timesheetKey, state: 'error' });
      }
    };
    load();
    return () => { active = false; };
  }, [timesheetKey, knownSubmission, hasBookingDetails, props.booking, props.onDetailsLoaded]);

  const recoveredSubmission = !hasBookingDetails && loadedSubmission?.timesheetKey === timesheetKey ? loadedSubmission : null;
  const booking = recoveredSubmission ? { ...props.booking, ...recoveredSubmission } : props.booking;
  const hasDetails = hasSubmissionDetails(booking, props.shift.timesheetTypeKey);
  const historicalSubmission = !!recoveredSubmission && recoveredSubmission.isCurrentSubmission !== true;
  const { paidAt, sentForPaymentAt, submittedStartTime, submittedEndTime, submittedOnSiteTime, submittedOffSiteTime, breaks, witnessName, witnessRole, canManageTimesheets, timesheetIsLocked } = booking;
  const timesheetStatus = props.archiveOverride === false ? props.restoredStatus : booking.timesheetStatus;
  const { timezone } = props.shift;
  const approved = timesheetStatus === 'approved';
  const archived = typeof props.archiveOverride === 'boolean' ? props.archiveOverride : props.archived || ((detailsLookup?.timesheetKey === timesheetKey && typeof detailsLookup.isArchived === 'boolean')
    ? detailsLookup.isArchived
    : booking.isArchived === true || booking.timesheetStatus === 'archived');
  const rejected = timesheetStatus === 'rejected';

  const breaksTotal = breaks?.length ? breaks.reduce((acc, curr) => {
    const breakStart = convertFromUtcToTimezone(curr.start, timezone);
    const breakEnd = convertFromUtcToTimezone(curr.end, timezone);
    const diff = breakEnd.diff(breakStart, 'minutes');
    return diff + acc;
  }, 0) : 'None';

  const candidateHasSubmittedStartTime = !!submittedStartTime || typeof submittedOnSiteTime === 'number';
  const candidateHasSubmittedEndTime = !!submittedEndTime || typeof submittedOffSiteTime === 'number';
  const buttonDisabled = props.isApprovingTimesheets || props.isRejectingTimesheets || (props.shiftAdminGroups && !props.adminIsInCareGroup);
  const submittedStartTimeIsSame = props.shift.timesheetTypeKey === 'start-end' && submittedStartTime ? moment(props.shift.startTime).isSame(moment(submittedStartTime)) : true;
  const submittedEndTimeIsSame = props.shift.timesheetTypeKey === 'start-end' && submittedEndTime ? moment(props.shift.endTime).isSame(moment(submittedEndTime)) : true;

  const expectedTotalTime = moment(props.shift.endTime).diff(moment(props.shift.startTime), 'minutes');
  let actualTotalTime = null;
  let totalTimesAreSame = true;
  if (hasDetails) {
    actualTotalTime = props.shift.timesheetTypeKey === 'start-end' ? minsToHours(moment(submittedEndTime).diff(moment(submittedStartTime), 'minutes')) : minsToHours(submittedOnSiteTime + submittedOffSiteTime);

    totalTimesAreSame = props.shift.timesheetTypeKey === 'start-end' ? moment(submittedEndTime).diff(moment(submittedStartTime), 'minutes') === expectedTotalTime : (submittedOnSiteTime + submittedOffSiteTime) === expectedTotalTime;
  }

  const showApproveButton = (!archived && !historicalSubmission && !approved && !paidAt && !sentForPaymentAt && canManageTimesheets) && (!timesheetIsLocked || props.canManageLockedTimesheets);
  const showRejectButton = (!archived && !historicalSubmission && !rejected && !paidAt && !sentForPaymentAt && canManageTimesheets) && (!timesheetIsLocked || props.canManageLockedTimesheets);

  return (
    <div className={`timesheet-submission${!hasDetails ? ' timesheet-submission--empty' : ''}`}>
      <h4 className="timesheet__header">Timesheet Submission</h4>
      {noSubmission && (
        <div className="timesheet-submission__empty">
          <div className="timesheet-submission__illustration" aria-hidden="true"><MdDescription size={64} /><MdAccessTime size={30} /></div>
          <h5>No submission yet</h5>
          <p className="timesheet-submission__info-text">The candidate has not submitted this timesheet.</p>
        </div>
      )}
      {!submissionRecorded && !noSubmission && !hasDetails && (
        <div className="timesheet-submission__empty" role="status">
          <h5>{loadingDetails ? 'Checking submission…' : 'Submission status unavailable'}</h5>
        </div>
      )}
      {submissionRecorded && !hasDetails && (
        <div className="timesheet-submission__empty" role="status">
          <h5>Submission recorded</h5>
          {loadingDetails && <p className="timesheet-submission__info-text">Loading submission details…</p>}
        </div>
      )}
      {!noSubmission && !hasDetails && !loadingDetails && (
        <div className="timesheet-submission__footer">
          <p className="timesheet-submission__warning" role="status">{submissionRecorded ? 'A submission is recorded, but its details could not be loaded.' : 'Unable to confirm whether this timesheet has been submitted.'}</p>
        </div>
      )}
      {!archived && timesheetIsLocked && props.canManageLockedTimesheets && canManageTimesheets && noSubmission && (
        <AdminTimesheetSubmission onExistingSubmission={markSubmissionRecorded} timesheetKey={props.booking.timesheetKey} shiftReference={props.shift.shiftRef || props.shift.shiftNumber} onSubmitted={props.onAdminSubmitted} />
      )}
      {hasDetails && (
        <>
          {props.shift.timesheetTypeKey === 'start-end-multi' ?
            <StartEndMultiTimesheetSubmission
              submittedMultiTimes={booking.submittedMultiTimes}
              shiftStartTime={props.shift.startTime}
              shiftEndTime={props.shift.endTime}
              witnessName={witnessName}
              witnessRole={witnessRole}
              timezone={timezone}
            />
            :
            <div className={`submission-table submission-table--${props.shift.timesheetTypeKey}`}>
              <div className="submission-table__header">
                <div className="submission-table__header-item" />
                <div className="submission-table__header-item">
                  <p className="submission-table__header-text">Grade</p>
                </div>
                <div className="submission-table__header-item">
                  <p className="submission-table__header-text">{props.shift.timesheetTypeKey === 'start-end' ? 'Start Time' : 'On-Site'}</p>
                </div>
                <div className="submission-table__header-item">
                  <p className="submission-table__header-text">{props.shift.timesheetTypeKey === 'start-end' ? 'End Time' : 'Off-Site'}</p>
                </div>
                <div className="submission-table__header-item">
                  <p className="submission-table__header-text">Total Time</p>
                </div>
                {props.shift.timesheetTypeKey === 'start-end' && (
                  <div className="submission-table__header-item">
                    <p className="submission-table__header-text">Breaks</p>
                  </div>
                )}
              </div>
              <div className="submission-table__row">
                <div className="submission-table__header-item">
                  <p className="submission-table__header-text">Expected</p>
                </div>
                <div className="submission-table__row-item">
                  <p className="submission-table__row-text">{props.shift?.gradeName ?? 'Any'}</p>
                </div>
                <div className="submission-table__row-item">
                  <p className="submission-table__row-text">{props.shift.timesheetTypeKey === 'start-end' ? formatTime(props.shift.startTime, timezone) : 'N/A'}</p>
                </div>
                <div className="submission-table__row-item">
                  <p className="submission-table__row-text">{props.shift.timesheetTypeKey === 'start-end' ? formatTime(props.shift.endTime, timezone) : 'N/A'}</p>
                </div>
                <div className="submission-table__row-item">
                  <p className="submission-table__row-text">{minsToHours(expectedTotalTime)}</p>
                </div>
                {props.shift.timesheetTypeKey === 'start-end' && (
                  <div className="submission-table__row-item">
                    <p className="submission-table__row-text" />
                  </div>
                )}
              </div>
              <div className="submission-table__row">
                <div className="submission-table__row-item">
                  <p className="submission-table__header-text">Submitted</p>
                </div>
                <div className="submission-table__row-item">
                  <p className="submission-table__row-text">{props?.booking?.gradeName}</p>
                </div>
                <div className="submission-table__row-item">
                  <p className={`submission-table__row-text${!submittedStartTimeIsSame ? '--red' : ''}`}>{candidateHasSubmittedStartTime ? ((submittedStartTime && formatTime(submittedStartTime, timezone)) || minsToHours(submittedOnSiteTime)) : 'None'}</p>
                </div>
                <div className="submission-table__row-item">
                  <p className={`submission-table__row-text${!submittedEndTimeIsSame ? '--red' : ''}`}>{candidateHasSubmittedEndTime ? ((submittedEndTime && formatTime(submittedEndTime, timezone)) || minsToHours(submittedOffSiteTime)) : 'None'}</p>
                </div>
                <div className="submission-table__row-item">
                  <p className={`submission-table__row-text${!totalTimesAreSame ? '--red' : ''}`}>{actualTotalTime}</p>
                </div>
                {props.shift.timesheetTypeKey === 'start-end' && (
                  <div className="submission-table__row-item">
                    <p className="submission-table__row-text">{`${breaksTotal}${breaksTotal === 'None' ? '' : 'mins'}`}</p>
                  </div>
                )}
              </div>
              {witnessName && witnessRole && (
                <div>
                  <p className="submission-table__header-text">Witnessed By: {witnessName} ({witnessRole})</p>
                </div>
              )}
            </div>
          }
          {historicalSubmission && <div className="timesheet-submission__footer"><p className="timesheet-submission__warning">This recorded submission is read-only.</p></div>}
          <div className="timesheet-submission__buttons">
            <p className={`timesheet-permission-message${props.timesheetPermissionsMessage ? ' timesheet-submission__warning' : ''}`}>{props.timesheetPermissionsMessage}</p>
            {(props.isApprovingTimesheets || props.isRejectingTimesheets) && <Loading size={30} />}
            {showRejectButton && (
              <Feature permissionRequired={permissions.TIMESHEETS_CAN_REJECT}>
                <Button
                  style={{ marginLeft: '3px', marginRight: '3px' }}
                  danger
                  large
                  onClick={() => props.openRejectModal()}
                  disabled={buttonDisabled}
                >
                  Reject
                </Button>
              </Feature>
            )}
            {showApproveButton && (
              <Feature permissionRequired={permissions.TIMESHEETS_CAN_APPROVE}>
                <Button
                  large
                  onClick={() => setApprovalModalOpen(true)}
                  disabled={buttonDisabled}
                >
                  Approve
                </Button>
              </Feature>
            )}
          </div>
          {approvalModalOpen && showApproveButton && !buttonDisabled && (
            <Feature permissionRequired={permissions.TIMESHEETS_CAN_APPROVE}>
              <TimesheetApproveModal
                declaration={props.timesheetApprovalDisclaimer}
                onClose={() => setApprovalModalOpen(false)}
                onConfirm={() => props.approveTimesheet(props.booking.timesheetKey, props.booking.shiftKey, props.booking.candidateKey)}
              />
            </Feature>
          )}
        </>
      )}
    </div>
  );
};
